import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { businessUnitService, BusinessUnit, type BusinessUnitInput } from '@/services/businessUnitService';

export const useBusinessUnits = (includeDeleted = false) => {
  return useQuery({
    queryKey: includeDeleted ? ['businessUnits', 'withDeleted'] : ['businessUnits'],
    queryFn: () => businessUnitService.getAll(includeDeleted).then(res => res.data),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useBusinessUnit = (id: number) => {
  return useQuery({
    queryKey: ['businessUnits', id],
    queryFn: () => businessUnitService.getById(id).then(res => res.data),
    enabled: !!id,
  });
};

export const useBusinessUnitSearch = (search: string, limit: number = 20) => {
  return useQuery({
    queryKey: ['businessUnits', 'search', search, limit],
    queryFn: () => businessUnitService.search(search, limit).then(res => res.data),
    staleTime: 30 * 1000, // 30 seconds for search results
    placeholderData: (previousData) => previousData, // Keep previous data while fetching
  });
};

// A duplicate code is reported inline by the form, so it needs no toast as well
const isCodeConflict = (error: unknown) => {
  const response = (error as { response?: { status?: number; data?: { detail?: string } } }).response;
  return response?.status === 409 && /code/i.test(response.data?.detail ?? '');
};

const errorMessage = (error: unknown, fallback: string) => {
  const err = error as { response?: { data?: { detail?: string; title?: string } } };
  return err.response?.data?.detail || err.response?.data?.title || fallback;
};

export const useCreateBusinessUnit = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BusinessUnitInput) => businessUnitService.create(data).then(res => res.data),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['businessUnits'] });
      toast.success(`Business unit "${created.name}" created`);
    },
    onError: (error: unknown) => {
      if (!isCodeConflict(error)) toast.error(errorMessage(error, 'Failed to create business unit'));
    },
  });
};

export const useUpdateBusinessUnit = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: BusinessUnitInput }) =>
      businessUnitService.update(id, data).then(res => res.data),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['businessUnits'] });
      // Contracts embed the business unit name, so refresh them too
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      toast.success(`Business unit "${updated.name}" updated`);
    },
    onError: (error: unknown) => {
      if (!isCodeConflict(error)) toast.error(errorMessage(error, 'Failed to update business unit'));
    },
  });
};

export const useDeleteBusinessUnit = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: businessUnitService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['businessUnits'] });
      toast.success('Business unit deleted successfully');
    },
    onError: (error: unknown) => toast.error(errorMessage(error, 'Failed to delete business unit')),
  });
};

export const useRestoreBusinessUnit = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: businessUnitService.restore,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['businessUnits'] });
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      toast.success('Business unit restored successfully');
    },
    onError: (error: unknown) => toast.error(errorMessage(error, 'Failed to restore business unit')),
  });
};

// Export the type for use in components
export type { BusinessUnit };
